import { http } from './http';
import type {
  BankAccountInput,
  CreateManualPaymentInput,
  CreateManualPaymentResult,
  DriverDeliveriesResponse,
  DriverDetail,
  DriverExportRow,
  KycDocuments,
  KycStatus,
  KycUploadInput,
  KycUploadResult,
  ManualPaymentDeliveryOption,
  ManualPaymentExportRow,
  ManualPaymentOrders,
  ManualPaymentsResponse,
  PayBalance,
  PayDriversFilters,
  PayDriversResponse,
  RepassesAccess,
  RepassesSettings,
  Topup,
  TransfersFilters,
  TransfersResponse,
} from '../types/repasses';

// BFF do Hermes (dashboard-api) → repassa pro pay-api. "Pay" abolido → Repasses.
// `http` = o `api` do Hermes + header `x-payment-provider` do seletor (SRV-137).
const BASE = '/v2/dashboard/repasses';

// ── Acesso (segurança: só brand_user) ─────────────────────────────
export const getAccess = () =>
  http.get<RepassesAccess>(`${BASE}/access`).then((r) => r.data);

// ── Entregadores ──────────────────────────────────────────────────
export const listDrivers = (filters: PayDriversFilters) =>
  http
    .get<PayDriversResponse>(`${BASE}/drivers`, { params: filters })
    .then((r) => r.data);

export const getBalance = () =>
  http.get<PayBalance>(`${BASE}/balance`).then((r) => r.data);

export const exportDrivers = (filters: PayDriversFilters) =>
  http
    .get<DriverExportRow[]>(`${BASE}/drivers/export`, { params: filters })
    .then((r) => r.data);

export const createDriverInvite = (body?: {
  ttl_hours?: number;
  no_expiry?: boolean;
}) =>
  http
    .post<{ token: string; url: string; expires_at: string | null }>(
      `${BASE}/drivers/invite`,
      body ?? {},
    )
    .then((r) => r.data);

export const getDriverDetail = (driverId: string) =>
  http.get<DriverDetail>(`${BASE}/drivers/${driverId}/detail`).then((r) => r.data);

export const getDriverDeliveries = (driverId: string, params: any) =>
  http
    .get<DriverDeliveriesResponse>(`${BASE}/drivers/${driverId}/deliveries`, {
      params,
    })
    .then((r) => r.data);

export const updateDriverBankAccount = (
  driverId: string,
  dto: { bank_account: BankAccountInput },
) =>
  http
    .patch<DriverDetail>(`${BASE}/drivers/${driverId}/bank-account`, dto)
    .then((r) => r.data);

export const refreshDriverBankStatus = (driverId: string) =>
  http
    .post(`${BASE}/drivers/${driverId}/bank-account/refresh`)
    .then((r) => r.data);

export const listManualPayments = (
  driverId: string,
  params: { page?: number; limit?: number },
) =>
  http
    .get<ManualPaymentsResponse>(`${BASE}/drivers/${driverId}/manual-payments`, {
      params,
    })
    .then((r) => r.data);

export const searchManualPaymentDeliveries = (
  driverId: string,
  params: { q?: string; limit?: number },
) =>
  http
    .get<ManualPaymentDeliveryOption[]>(
      `${BASE}/drivers/${driverId}/manual-payments/deliveries`,
      { params },
    )
    .then((r) => r.data);

export const getManualPaymentOrders = (driverId: string, paymentId: string) =>
  http
    .get<ManualPaymentOrders>(
      `${BASE}/drivers/${driverId}/manual-payments/${paymentId}/orders`,
    )
    .then((r) => r.data);

/** Até 5.000 lançamentos, do mais novo ao mais antigo. Datas YYYY-MM-DD (Brasília). */
export const exportManualPayments = (params: {
  driverId?: string;
  dateFrom?: string;
  dateTo?: string;
}) =>
  http
    .get<ManualPaymentExportRow[]>(`${BASE}/manual-payments/export`, { params })
    .then((r) => r.data);

export const createManualPayment = (
  driverId: string,
  dto: CreateManualPaymentInput,
) =>
  http
    .post<CreateManualPaymentResult>(
      `${BASE}/drivers/${driverId}/manual-payments`,
      dto,
    )
    .then((r) => r.data);

// ── Ações do entregador ───────────────────────────────────────────
export const approveDriverTransfer = (driverId: string) =>
  http.post(`${BASE}/drivers/${driverId}/approve-transfer`).then((r) => r.data);

export const approveDriverDeliveries = (driverId: string) =>
  http.post(`${BASE}/drivers/${driverId}/approve-deliveries`).then((r) => r.data);

export const pauseDriverDeliveries = (driverId: string, reason?: string) =>
  http
    .post(`${BASE}/drivers/${driverId}/pause-deliveries`, { reason })
    .then((r) => r.data);

// ── Transferências ────────────────────────────────────────────────
export const listTransfers = (filters: TransfersFilters) =>
  http
    .get<TransfersResponse>(`${BASE}/transfers`, { params: filters })
    .then((r) => r.data);

export const retryTransfer = (transferId: string) =>
  http.post(`${BASE}/transfers/${transferId}/retry`).then((r) => r.data);

// ── Ações de pagamento (repasse por entrega) ──────────────────────
export const pausePayment = (id: string, reason?: string) =>
  http.patch(`${BASE}/payments/${id}/pause`, { reason }).then((r) => r.data);

export const resumePayment = (id: string) =>
  http.patch(`${BASE}/payments/${id}/resume`).then((r) => r.data);

export const denyPayment = (id: string, reason?: string) =>
  http.patch(`${BASE}/payments/${id}/deny`, { reason }).then((r) => r.data);

export const approvePayment = (id: string) =>
  http.patch(`${BASE}/payments/${id}/approve`).then((r) => r.data);

// ── Configurações ─────────────────────────────────────────────────
export const getSettings = () =>
  http.get<RepassesSettings>(`${BASE}/settings`).then((r) => r.data);

export const updateSettings = (dto: { withdrawal_day?: number }) =>
  http
    .patch<{ withdrawal_day: number }>(`${BASE}/settings`, dto)
    .then((r) => r.data);

// ── Saldo / Recarga ───────────────────────────────────────────────
export const createTopup = (dto: {
  valueReais: number;
  billingType?: 'PIX' | 'BOLETO';
}) =>
  http
    .post<Topup>(`${BASE}/asaas/topup`, dto, { timeout: 60_000 })
    .then((r) => r.data);

export const getTopup = (id: string) =>
  http.get<Topup>(`${BASE}/asaas/topup/${id}`).then((r) => r.data);

// ── KYC (verificação da conta da marca) ───────────────────────────
// Sempre a conta ASAAS da marca, independente do seletor de provedor: o
// pay-api resolve pela credencial Asaas (SRV-133), não pelo provedor padrão.
export const getKycStatus = () =>
  http.get<KycStatus>(`${BASE}/kyc/status`).then((r) => r.data);

export const getKycDocuments = () =>
  http.get<KycDocuments>(`${BASE}/kyc/documents`).then((r) => r.data);

/** Repergunta o status à Asaas e persiste — mesma forma do `kyc/status`. */
export const refreshKyc = () =>
  http.post<KycStatus>(`${BASE}/kyc/refresh`).then((r) => r.data);

export const uploadKycDocument = (dto: KycUploadInput) =>
  http
    .post<KycUploadResult>(`${BASE}/kyc/documents/upload`, dto, {
      // Arquivo em base64 no corpo: o default do Hermes aperta em foto grande.
      timeout: 60_000,
    })
    .then((r) => r.data);
